<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toast-notification";

import { getApiErrorMessage } from "@/helpers/error";

import Button from "@/dsComponents/buttons/Button.vue";
import FormSection from "@/dsComponents/FormSection.vue";

import { AskedChangeAction, type AskedChangeDisplayItem } from "./types";
import ValidateItem from "./ValidateItem.vue";
import { submitWorkerAskedChangesDecisions } from "./workerAskedChangesService";

interface ValidateJobsItemsProps {
    askedJobsChanges: AskedChangeDisplayItem[];
    workerId: string;
    separator: boolean;
}

const props = defineProps<ValidateJobsItemsProps>();

const emit = defineEmits<{
    "validated-item": [];
}>();

const { t } = useI18n();
const toaster = useToast();

const actionDone = ref<"" | "validated" | "refused">("");

const handleAskedJobsChanges = async ({ action }: { action: "reject" | "validate" }) => {
    try {
        await submitWorkerAskedChangesDecisions(props.workerId, {
            decisions: [{ field: "jobs", action: action === "validate" ? AskedChangeAction.VALIDATE : AskedChangeAction.REJECT }],
        });
        actionDone.value = action === "validate" ? "validated" : "refused";
        toaster?.open({
            message: t(`page.worker.askedChanges.message.${action}`) as string,
            type: "success",
            duration: 5000,
        });
        emit("validated-item");
    } catch (error) {
        toaster?.open({
            message: getApiErrorMessage(error, "page.worker.askedChanges.message.error"),
            type: "error",
            duration: 5000,
        });
    }
};
</script>
<template>
    <div class="validate-bloc">
        <FormSection :title="t('form.jobs')" size="sm" :separator="separator" />

        <!-- biome-ignore lint/correctness/useVueVForKey: Init Biome -->
        <ValidateItem v-for="item in askedJobsChanges" :item="item" />

        <div class="button-container">
            <Button
                v-if="!actionDone"
                full-width
                size="sm"
                variant="stroke"
                type="neutral"
                :label="t('page.worker.askedChanges.refuseAll')"
                @click="handleAskedJobsChanges({ action: 'reject' })"
            />
            <Button
                v-if="!actionDone"
                full-width
                size="sm"
                variant="filled"
                type="primary"
                :label="t('global.validateAll')"
                @click="handleAskedJobsChanges({ action: 'validate' })"
            />
            <Button
                v-if="actionDone"
                full-width
                size="sm"
                variant="filled"
                type="primary"
                disabled
                :label="t(`page.worker.askedChanges.${actionDone}`)"
                right-icon="check"
            />
        </div>
    </div>
</template>
<style lang="scss" scoped>
.validate-bloc {
    display: flex;
    flex-direction: column;
    gap: 24px;

    .button-container {
        width: 100%;
        justify-content: center;

        display: flex;
        gap: 18px;
    }
}
</style>
